import React, { FC, memo } from "react";
import { Navigate, Outlet, useRoutes as reactRouterUseRoutes } from "react-router";
import { Spinner } from "@lookiero/aurora";
import { I18n, OnI18nErrorFn } from "@lookiero/i18n-react";
import { LookAndLike } from "@lookiero/look-and-like";
import { Kameleoon } from "@lookiero/sty-psp-ab-testing";
import { Locale } from "@lookiero/sty-psp-locale";
import { Layout } from "@lookiero/sty-psp-ui";
import { Tradename } from "@lookiero/sty-sp-tradename";
import { Customer } from "../../../projection/customer/customer";
import { KameleoonEnvironment } from "../../ab-testing/kameleoonEnvironment";
import { StaticInfoProvider } from "../hooks/useStaticInfo";
import { App } from "../views/App";
import { Home } from "../views/home/Home";
import { NotFound } from "../views/notFound/NotFound";
import { RouteScreen } from "../views/screen/RouteScreen";
import { Routes } from "./routes";

interface RoutingProps {
  readonly apiUrl: () => string;
  readonly getAuthToken: () => Promise<string>;
  readonly basePath?: string;
  readonly customer: Customer | undefined;
  readonly I18n: I18n;
  readonly kameleoon: KameleoonEnvironment;
  readonly layout: Layout;
  readonly locale: Locale;
  readonly androidInAppBrowser: boolean;
  readonly lookAndLike: LookAndLike;
  readonly tradename: Tradename;
  readonly onBack: () => void;
  readonly onI18nError?: OnI18nErrorFn;
  readonly useRoutes: typeof reactRouterUseRoutes;
}

const Routing: FC<RoutingProps> = ({
  apiUrl,
  getAuthToken,
  basePath = "",
  customer,
  I18n,
  kameleoon,
  layout,
  locale,
  androidInAppBrowser,
  lookAndLike,
  tradename,
  onBack,
  onI18nError,
  useRoutes = reactRouterUseRoutes,
}) => {
  return useRoutes([
    {
      path: "",
      element: (
        <StaticInfoProvider
          androidInAppBrowser={androidInAppBrowser}
          apiUrl={apiUrl}
          basePath={basePath}
          customer={customer as Customer}
          getAuthToken={getAuthToken}
          kameleoon={kameleoon}
          tradename={tradename}
        >
          <I18n loader={<Spinner />} locale={locale} onError={onI18nError}>
            <Kameleoon
              consented={kameleoon.consented}
              experiments={kameleoon.experiments}
              siteCode={kameleoon.siteCode}
            >
              <App>
                <Outlet />
              </App>
            </Kameleoon>
          </I18n>
        </StaticInfoProvider>
      ),
      children: [
        {
          path: Routes.HOME,
          element: (
            <Home layout={layout} lookAndLike={lookAndLike} onBack={onBack}>
              <Outlet />
            </Home>
          ),
          children: [
            {
              path: Routes.SCREEN,
              element: <RouteScreen />,
            },
          ],
        },
        {
          path: Routes.NOT_FOUND,
          element: <NotFound layout={layout} />,
        },
        {
          path: "*",
          element: <Navigate to={`${Routes.HOME}`} replace />,
        },
      ],
    },
  ]);
};

/**
 * Provided useRoutes is not stable (when integrated with Sentry) as it's rendering a different component tree.
 *
 * https://github.com/getsentry/sentry-javascript/blob/master/packages/react/src/reactrouterv6.tsx#L221
 * (SentryRoutes is a new component after each re-render)
 */
const MemoizedRouting = memo(Routing);
export { MemoizedRouting as Routing };
